/**
 * TeamJobsSection: the jobs staff can start for a team outside their
 * schedule. One row per job in the API's registry
 * (api/app/services/team_jobs.py), which owns each job's name, its
 * description and the reason a run is refused, so a new job is one
 * registry entry there and no change here.
 *
 * A row says whether the team has a run active and how its last one
 * ended, straight from the cluster's Jobs. The cluster is the only record
 * of a run and keeps a finished one for about three days.
 *
 * The run button is off, with its reason, while a run is active or the
 * team's config has the job switched off. A job that writes outside the
 * platform (eval PRs open pull requests on the customer's repository)
 * asks first. A job with a start option (DateBench variants: vary every
 * case again) asks too, with the option as a checkbox that starts off.
 * The mutation lives here, not in the dialog, so its answer lands even
 * if the dialog has closed (ConfirmDialog).
 *
 * A row with a run has a Logs button, which opens the log of the run the
 * row shows (TeamJobLogSheet): the active one, else the last. A row with
 * an active run has a Stop button, which asks first (TeamJobStopDialog),
 * and is off with its reason when the API would refuse the stop.
 */

import { Loader2, Play, ScrollText, Square } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { useRunTeamJobApiInternalTeamsTeamIdJobsJobSlugRunPost } from '@/api/generated/internal/internal'
import type { JobRun, TeamJobItem, TeamJobOption } from '@/api/generated/model'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { jobBlocker, useTeamJobs } from '@/hooks/useTeamJobs'

import type { TeamConfigSectionProps } from './sections'
import { type RunTarget, TeamJobLogSheet } from './TeamJobLogSheet'
import { TeamJobStopDialog } from './TeamJobStopDialog'

export function TeamJobsSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const { jobs, isLoading, error, refresh } = useTeamJobs(team.id)
  // The job asked about stays after the dialog closes, so the question
  // does not blank out while the dialog fades.
  const [asked, setAsked] = useState<TeamJobItem | null>(null)
  const [isAsking, setIsAsking] = useState(false)
  const [withOption, setWithOption] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  // Kept after the sheet closes, as `asked` is after the dialog does.
  const [logTarget, setLogTarget] = useState<RunTarget | null>(null)
  const [isLogOpen, setIsLogOpen] = useState(false)
  const [stopTarget, setStopTarget] = useState<RunTarget | null>(null)
  const [isStopOpen, setIsStopOpen] = useState(false)
  const run = useRunTeamJobApiInternalTeamsTeamIdJobsJobSlugRunPost({
    mutation: {
      onSuccess: (response, variables) => {
        const started = jobs.find((job) => job.slug === variables.jobSlug)
        if (response.status === 200) {
          toast.success(`${started?.display_name ?? variables.jobSlug} started (${response.data.job_name})`)
        }
        setIsAsking(false)
      },
      onError: (failure: unknown) => {
        const message = getApiErrorDetail(failure) ?? 'The run did not start.'
        if (isAsking) setConfirmError(message)
        else toast.error(message)
      },
      // A refused run says the cluster moved on, so read it again either way.
      onSettled: refresh,
    },
  })
  const start = (job: TeamJobItem, option: boolean): void => {
    setConfirmError(null)
    run.mutate({ teamId: team.id, jobSlug: job.slug, data: { with_option: option } })
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <SettingsSection
        title="Run now"
        description="Each run uses the scheduled job's image and settings and works on this team only."
      >
        {isLoading ? (
          <Skeleton className="h-14 w-full" />
        ) : error !== null ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : (
          <SettingsList>
            {jobs.map((job) => (
              <JobRow
                key={job.slug}
                job={job}
                isStarting={run.isPending && run.variables.jobSlug === job.slug}
                disabled={run.isPending}
                onLogs={(jobName) => {
                  setLogTarget({ jobSlug: job.slug, displayName: job.display_name, jobName })
                  setIsLogOpen(true)
                }}
                onStop={(jobName) => {
                  setStopTarget({ jobSlug: job.slug, displayName: job.display_name, jobName })
                  setIsStopOpen(true)
                }}
                onRun={() => {
                  if (job.confirmation === null && job.option === null) {
                    start(job, false)
                    return
                  }
                  setConfirmError(null)
                  setWithOption(false)
                  setAsked(job)
                  setIsAsking(true)
                }}
              />
            ))}
          </SettingsList>
        )}
      </SettingsSection>

      <ConfirmDialog
        open={isAsking}
        onOpenChange={setIsAsking}
        title={asked === null ? '' : `Run ${asked.display_name} for ${team.name}?`}
        description={asked === null ? null : asked.confirmation}
        confirmLabel="Run"
        pendingLabel="Starting..."
        isPending={run.isPending}
        error={confirmError}
        onConfirm={() => {
          if (asked !== null) start(asked, withOption)
        }}
      >
        {asked !== null && asked.option !== null && (
          <StartOptionField
            option={asked.option}
            checked={withOption}
            disabled={run.isPending}
            onCheckedChange={setWithOption}
          />
        )}
      </ConfirmDialog>

      <TeamJobLogSheet
        teamId={team.id}
        target={logTarget}
        open={isLogOpen}
        onOpenChange={setIsLogOpen}
      />

      <TeamJobStopDialog
        teamId={team.id}
        teamName={team.name}
        target={stopTarget}
        open={isStopOpen}
        onOpenChange={setIsStopOpen}
        onSettled={refresh}
      />
    </div>
  )
}

/** A job's one start choice, off until staff tick it. */
export function StartOptionField({
  option,
  checked,
  disabled,
  onCheckedChange,
}: {
  option: TeamJobOption
  checked: boolean
  disabled: boolean
  onCheckedChange: (checked: boolean) => void
}): React.JSX.Element {
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id="job-start-option"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        aria-describedby="job-start-option-description"
        onCheckedChange={(next) => {
          onCheckedChange(next === true)
        }}
      />
      <div className="space-y-1">
        <Label htmlFor="job-start-option" className="text-sm font-normal">
          {option.label}
        </Label>
        <p id="job-start-option-description" className="text-xs text-muted-foreground">
          {option.description}
        </p>
      </div>
    </div>
  )
}

/** The run the row shows, active or last, or null when the team has none. */
function shownRun(job: TeamJobItem): string | null {
  return job.active_job_name ?? job.last_run?.job_name ?? null
}

function JobRow({
  job,
  isStarting,
  disabled,
  onLogs,
  onStop,
  onRun,
}: {
  job: TeamJobItem
  isStarting: boolean
  disabled: boolean
  onLogs: (jobName: string) => void
  onStop: (jobName: string) => void
  onRun: () => void
}): React.JSX.Element {
  const activeName = job.active_job_name
  const blocked = jobBlocker(job)
  const runName = shownRun(job)
  return (
    <SettingsListRow className="items-start py-3">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium text-foreground">{job.display_name}</p>
        <p className="text-xs text-muted-foreground">{job.description}</p>
        <JobStatus job={job} />
      </div>
      <div className="flex shrink-0 gap-2">
        {runName !== null && (
          <Button
            variant="outline"
            size="sm"
            aria-label={`Open the log of ${runName}`}
            onClick={() => {
              onLogs(runName)
            }}
          >
            <ScrollText aria-hidden="true" />
            Logs
          </Button>
        )}
        {activeName !== null && (
          <Button
            variant="outline"
            size="sm"
            aria-label={`Stop ${activeName}`}
            title={job.stop_refusal ?? undefined}
            disabled={job.stop_refusal !== null}
            onClick={() => {
              onStop(activeName)
            }}
          >
            <Square aria-hidden="true" />
            Stop
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          aria-label={`Run ${job.display_name} now`}
          title={blocked ?? undefined}
          disabled={disabled || blocked !== null}
          onClick={onRun}
        >
          {isStarting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Play aria-hidden="true" />}
          Run now
        </Button>
      </div>
    </SettingsListRow>
  )
}

interface ChipSpec {
  label: string
  variant: BadgeProps['variant']
}

/** The batch tables' dialect: a hairline while moving, a tint once settled. */
const OUTCOME_CHIP: Record<JobRun['outcome'], ChipSpec> = {
  active: { label: 'Running', variant: 'default' },
  succeeded: { label: 'Succeeded', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' },
}

function formatStarted(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * What the cluster says about the team's runs of one job, then the reason
 * the job cannot run when there is one. The reason is in ink: it is the
 * line the reader has to act on.
 */
function JobStatus({ job }: { job: TeamJobItem }): React.JSX.Element {
  return (
    <>
      {job.active_job_name !== null ? (
        <RunLine label="Active run" outcome="active" jobName={job.active_job_name} startedAt={null} />
      ) : job.last_run !== null ? (
        <RunLine
          label="Last run"
          outcome={job.last_run.outcome}
          jobName={job.last_run.job_name}
          startedAt={job.last_run.started_at}
        />
      ) : (
        <p className="text-xs text-muted-foreground">This team has no recent run.</p>
      )}
      {job.unavailable_reason !== null && (
        <p className="text-xs text-foreground">{job.unavailable_reason}</p>
      )}
    </>
  )
}

function RunLine({
  label,
  outcome,
  jobName,
  startedAt,
}: {
  label: string
  outcome: JobRun['outcome']
  jobName: string
  startedAt: string | null
}): React.JSX.Element {
  const chip = OUTCOME_CHIP[outcome]
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span>{label}</span>
      <Badge variant={chip.variant}>{chip.label}</Badge>
      {startedAt !== null && <span>{formatStarted(startedAt)}</span>}
      <span className="break-all font-mono">{jobName}</span>
    </div>
  )
}
